/**
 * 外呼拨打按钮组件
 */

import React, { useState, useEffect } from 'react';
import { Button, Modal, message, Steps, Space, Typography, Tag, Input, Form } from 'antd';
import { PhoneOutlined, LoadingOutlined, StopOutlined } from '@ant-design/icons';
import { makeCall, getCallRecord, getMySeat } from '../../services/outbound';
import { CallStatus, OutboundSeat } from '../../types/outbound';
import { addPhoneBlacklist, checkPhoneBlacklist } from '../../services/phoneBlacklist';

const { TextArea } = Input;

const { Text } = Typography;

interface OutboundCallButtonProps {
  leadId: number;
  leadName: string;
  phone: string;
  onCallComplete?: () => void;
  onCancel?: () => void; // 取消拨打的回调
  onOpenLeadDetail?: (leadId: number) => void; // 打开线索详情的回调
  autoOpen?: boolean; // 是否自动打开确认弹窗
  hideButton?: boolean; // 是否隐藏按钮，只显示弹窗
}

const OutboundCallButton: React.FC<OutboundCallButtonProps> = ({
  leadId,
  leadName,
  phone,
  onCallComplete,
  onCancel,
  onOpenLeadDetail,
  autoOpen = false,
  hideButton = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [callId, setCallId] = useState<number | null>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>(CallStatus.INITIATED);
  const [mySeat, setMySeat] = useState<OutboundSeat | null>(null);
  const [checkingSeat, setCheckingSeat] = useState(false);
  const [blacklistModalVisible, setBlacklistModalVisible] = useState(false);
  const [isBlacklisted, setIsBlacklisted] = useState(false);
  const [blacklistRemark, setBlacklistRemark] = useState<string>('');
  const [form] = Form.useForm();

  // 检查座席状态和黑名单状态
  useEffect(() => {
    checkSeatStatus();
    checkBlacklistStatus();
  }, [phone]);

  // 自动打开确认弹窗 - 监听leadId变化
  useEffect(() => {
    if (autoOpen && leadId) {
      setConfirmVisible(true);
    }
  }, [autoOpen, leadId]);

  const checkSeatStatus = async () => {
    setCheckingSeat(true);
    try {
      const seat = await getMySeat();
      setMySeat(seat || null);
    } catch (error: any) {
      console.error('检查座席状态失败:', error);
      setMySeat(null);
    } finally {
      setCheckingSeat(false);
    }
  };

  // 检查黑名单状态
  const checkBlacklistStatus = async () => {
    if (!phone) return;
    try {
      const result = await checkPhoneBlacklist(phone);
      setIsBlacklisted(result.is_blacklisted);
      if (result.is_blacklisted && result.remark) {
        setBlacklistRemark(result.remark);
      }
    } catch (error) {
      console.error('检查黑名单状态失败:', error);
    }
  };

  // 添加到黑名单
  const handleAddToBlacklist = () => {
    form.resetFields();
    setBlacklistModalVisible(true);
  };

  // 提交黑名单
  const handleBlacklistSubmit = async () => {
    try {
      const values = await form.validateFields();
      await addPhoneBlacklist({
        phone,
        remark: values.remark,
      });
      message.success('已添加到黑名单');
      setBlacklistModalVisible(false);
      setConfirmVisible(false);
      setIsBlacklisted(true);
      onCancel?.();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('添加黑名单失败');
      }
    }
  };

  // 轮询获取通话状态
  useEffect(() => {
    if (!callId || !statusVisible) return;

    const timer = setInterval(async () => {
      try {
        const record = await getCallRecord(callId);
        setCallStatus(record.status);

        // 如果通话结束，停止轮询
        if (
          [CallStatus.COMPLETED, CallStatus.FAILED, CallStatus.CANCELLED].includes(
            record.status
          )
        ) {
          clearInterval(timer);
          setTimeout(() => {
            setStatusVisible(false);
            onCallComplete?.();
          }, 3000);
        }
      } catch (error) {
        console.error('获取通话状态失败', error);
      }
    }, 2000);

    return () => clearInterval(timer);
  }, [callId, statusVisible]);

  // 发起外呼
  const handleCall = async () => {
    setLoading(true);
    try {
      const response = await makeCall({
        lead_id: leadId,
        callee: phone,
        max_duration: 60,
      });

      setCallId(response.id);
      setCallStatus(response.status);
      setConfirmVisible(false);
      setStatusVisible(true);
      message.success('外呼已发起，请注意接听电话');
    } catch (error: any) {
      // 从axios错误响应中提取detail
      const errorData = error.response?.data;
      const errorDetail = errorData?.detail;
      
      // 检查是否是需要添加跟进记录的错误（detail是对象且包含lead_id）
      if (errorDetail && typeof errorDetail === 'object' && errorDetail.lead_id) {
        const { message: errorMsg, lead_id, phone: errorPhone } = errorDetail;
        
        // 关闭确认拨打弹窗
        setConfirmVisible(false);
        
        // 延迟一下再显示新弹窗，避免冲突
        setTimeout(() => {
          Modal.confirm({
            title: '需要添加跟进记录',
            content: errorMsg,
            okText: '去添加跟进',
            cancelText: '取消',
            onOk: () => {
              // 调用取消回调
              onCancel?.();
              // 打开线索详情（侧边抽屉）
              if (onOpenLeadDetail) {
                onOpenLeadDetail(lead_id);
              }
            },
            onCancel: () => {
              // 用户取消时也调用取消回调
              onCancel?.();
            },
          });
        }, 100);
      }
      // 其他错误由request拦截器统一处理，这里不再重复显示
    } finally {
      setLoading(false);
    }
  };

  // 获取状态步骤
  const getStatusStep = () => {
    const steps = [
      { status: CallStatus.INITIATED, title: '发起呼叫' },
      { status: CallStatus.CALLER_RINGING, title: '主叫振铃' },
      { status: CallStatus.CALLER_ANSWERED, title: '主叫接听' },
      { status: CallStatus.CALLEE_RINGING, title: '被叫振铃' },
      { status: CallStatus.CONNECTED, title: '通话中' },
      { status: CallStatus.COMPLETED, title: '通话结束' },
    ];

    const currentIndex = steps.findIndex((s) => s.status === callStatus);
    return { steps, currentIndex };
  };

  // 渲染状态标签
  const renderStatusTag = () => {
    const statusMap: Record<CallStatus, { text: string; color: string }> = {
      [CallStatus.INITIATED]: { text: '发起中', color: 'processing' },
      [CallStatus.CALLER_RINGING]: { text: '主叫振铃', color: 'processing' },
      [CallStatus.CALLER_ANSWERED]: { text: '主叫接听', color: 'processing' },
      [CallStatus.CALLEE_RINGING]: { text: '被叫振铃', color: 'processing' },
      [CallStatus.CALLEE_ANSWERED]: { text: '被叫接听', color: 'processing' },
      [CallStatus.CONNECTED]: { text: '通话中', color: 'success' },
      [CallStatus.COMPLETED]: { text: '已完成', color: 'success' },
      [CallStatus.FAILED]: { text: '失败', color: 'error' },
      [CallStatus.CANCELLED]: { text: '已取消', color: 'default' },
    };

    const status = statusMap[callStatus] || { text: callStatus, color: 'default' };
    return <Tag color={status.color}>{status.text}</Tag>;
  };

  // 检查是否可以拨打（不考虑黑名单，黑名单只在确认对话框中处理）
  const canMakeCall = () => {
    if (!mySeat) return false;
    if (mySeat.status !== 'active') return false;
    if (!mySeat.is_verified) return false;
    return true;
  };

  // 获取不可拨打的原因
  const getDisabledReason = () => {
    if (checkingSeat) return '检查座席状态中...';
    if (!mySeat) return '您还没有座席，请先在配置中心创建座席';
    if (mySeat.status !== 'active') return '座席未激活';
    if (!mySeat.is_verified) return '座席未验证，请回复验证短信激活座席';
    return '';
  };

  const { steps, currentIndex } = getStatusStep();

  return (
    <>
      {!hideButton && (
        <Button
          type="primary"
          icon={<PhoneOutlined />}
          onClick={() => setConfirmVisible(true)}
          loading={checkingSeat}
          disabled={!canMakeCall()}
          title={getDisabledReason()}
        >
          拨打
        </Button>
      )}

      {/* 确认拨打对话框 */}
      <Modal
        title="确认拨打"
        open={confirmVisible}
        onCancel={() => {
          setConfirmVisible(false);
          onCancel?.();
        }}
        onOk={handleCall}
        confirmLoading={loading}
        okText="确认拨打"
        okButtonProps={{ disabled: isBlacklisted }}
        cancelText="取消"
        footer={[
          !isBlacklisted && (
            <Button key="blacklist" danger icon={<StopOutlined />} onClick={handleAddToBlacklist}>
              标记为黑名单
            </Button>
          ),
          <Button key="cancel" onClick={() => {
            setConfirmVisible(false);
            onCancel?.();
          }}>
            取消
          </Button>,
          <Button
            key="ok"
            type="primary"
            loading={loading}
            disabled={isBlacklisted}
            onClick={handleCall}
          >
            确认拨打
          </Button>,
        ]}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {isBlacklisted && (
            <div
              style={{
                padding: 12,
                background: '#fff2e8',
                border: '1px solid #ffbb96',
                borderRadius: 4,
              }}
            >
              <Text type="danger">
                <strong>该号码已在黑名单中</strong>
                {blacklistRemark && (
                  <>
                    <br />
                    备注：{blacklistRemark}
                  </>
                )}
              </Text>
            </div>
          )}
          <div>
            <Text strong>线索名称：</Text>
            <Text>{leadName}</Text>
          </div>
          <div>
            <Text strong>拨打号码：</Text>
            <Text copyable>{phone}</Text>
          </div>
          <div>
            <Text strong>主叫号码：</Text>
            <Text>{mySeat?.bind_number}</Text>
          </div>
          <div
            style={{
              padding: 12,
              background: '#f0f5ff',
              border: '1px solid #adc6ff',
              borderRadius: 4,
            }}
          >
            <Text type="secondary">
              <strong>拨打流程：</strong>
              <br />
              1. 系统会先呼叫您的手机（{mySeat?.bind_number}）
              <br />
              2. 您接听后，系统会呼叫客户号码
              <br />
              3. 客户接听后，双方建立通话
              <br />
              4. 通话会自动录音，录音将保存在系统中
            </Text>
          </div>
        </Space>
      </Modal>

      {/* 添加黑名单对话框 */}
      <Modal
        title="标记为黑名单"
        open={blacklistModalVisible}
        onOk={handleBlacklistSubmit}
        onCancel={() => setBlacklistModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Text strong>手机号：</Text>
            <Text>{phone}</Text>
          </div>
          <Form form={form} layout="vertical">
            <Form.Item label="备注" name="remark">
              <TextArea rows={4} placeholder="请输入拉黑原因或备注信息" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* 通话状态对话框 */}
      <Modal
        title="通话状态"
        open={statusVisible}
        footer={null}
        onCancel={() => setStatusVisible(false)}
        width={500}
      >
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Space size={8}>
              <Text strong>当前状态：</Text>
              {renderStatusTag()}
            </Space>
          </div>

          <Steps
            direction="vertical"
            current={currentIndex}
            items={steps.map((step) => ({
              title: step.title,
              icon:
                step.status === callStatus ? (
                  <LoadingOutlined />
                ) : undefined,
            }))}
          />

          {callStatus === CallStatus.COMPLETED && (
            <div
              style={{
                padding: 12,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Text type="success">通话已完成，录音将在几分钟后可用</Text>
            </div>
          )}

          {callStatus === CallStatus.FAILED && (
            <div
              style={{
                padding: 12,
                background: '#fff2e8',
                border: '1px solid #ffbb96',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Text type="danger">通话失败，请稍后重试</Text>
            </div>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default OutboundCallButton;
